// Block Editor Types

export type BlockType =
  | 'text'
  | 'heading'
  | 'image'
  | 'price_table'
  | 'quote'
  | 'divider'
  | 'spacer'
  | 'video'
  | 'gallery'
  | 'list'
  | 'signature'
  | 'cover'
  | 'terms'
  | 'dual_signature'
  | 'benefits'
  | 'timeline'
  | 'team'
  | 'testimonials'
  | 'packages'
  | 'faq'
  | 'client_logos'
  | 'stats'
  | 'attachments'
  | 'html';

export interface BaseBlock {
  id: string;
  type: BlockType;
  order: number;
}

export interface TextBlock extends BaseBlock {
  type: 'text';
  content: string;
  title?: string;
  layout?: 'standard' | 'title-right' | 'title-left';
  alignment?: 'right' | 'center' | 'left';
  titleAlignment?: 'right' | 'center' | 'left';
  fontSize?: 'small' | 'normal' | 'large' | 'xlarge';
  titleFontSize?: 'small' | 'normal' | 'large' | 'xlarge';
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline';
  color?: string;
  titleColor?: string;
}

export interface HeadingBlock extends BaseBlock {
  type: 'heading';
  content: string;
  level: 1 | 2 | 3;
  alignment?: 'right' | 'center' | 'left';
  fontWeight?: 'normal' | 'bold';
  fontStyle?: 'normal' | 'italic';
  textDecoration?: 'none' | 'underline';
  color?: string;
}

export interface ImageBlock extends BaseBlock {
  type: 'image';
  src: string;
  alt?: string;
  width?: 'full' | 'medium' | 'small';
  alignment?: 'right' | 'center' | 'left';
}

export interface PriceTableBlock extends BaseBlock {
  type: 'price_table';
  items: PricingItem[];
  showQuantity?: boolean;
  showUnitPrice?: boolean;
  alignment?: 'right' | 'center' | 'left';
}

export interface PricingItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

export interface QuoteBlock extends BaseBlock {
  type: 'quote';
  content: string;
  author?: string;
  alignment?: 'right' | 'center' | 'left';
}

export interface DividerBlock extends BaseBlock {
  type: 'divider';
  style?: 'solid' | 'dashed' | 'dotted';
}

export interface SpacerBlock extends BaseBlock {
  type: 'spacer';
  height: 'small' | 'medium' | 'large';
}

export interface VideoBlock extends BaseBlock {
  type: 'video';
  url: string; // YouTube or Vimeo URL
}

export interface GalleryBlock extends BaseBlock {
  type: 'gallery';
  images: { src: string; alt?: string }[];
  columns: 2 | 3 | 4;
}

export interface ListBlock extends BaseBlock {
  type: 'list';
  items: string[];
  style: 'bullet' | 'numbered' | 'check';
  alignment?: 'right' | 'center' | 'left';
}

export interface SignatureBlock extends BaseBlock {
  type: 'signature';
  label: string;
  required: boolean;
  signatureData?: string;
}

export interface CoverBlock extends BaseBlock {
  type: 'cover';
  // Background
  backgroundImage?: string;
  backgroundColor?: string;
  overlay?: boolean;
  overlayOpacity?: number; // 0-100

  // Logo
  logoUrl?: string;
  logoPosition?: 'top-right' | 'top-left' | 'top-center';
  logoSize?: 'small' | 'medium' | 'large';

  // Main content
  title: string;
  subtitle?: string;

  // Client info
  preparedFor?: string;
  clientName?: string;

  // Date
  showDate?: boolean;
  dateValue?: string;

  // Contact info
  showContact?: boolean;
  preparedBy?: string;
  contactEmail?: string;
  contactPhone?: string;

  // Layout
  coverSize?: 'top' | 'full'; // top = partial page, full = full page
  textAlignment?: 'right' | 'center' | 'left';
}

// Terms theme types
export type TermsTheme = 'payment' | 'legal' | 'warranty' | 'cancellation' | 'privacy' | 'delivery' | 'general' | 'warning' | 'info';
export type TermsVariant = 'filled' | 'outlined' | 'minimal' | 'card';
export type TermsListStyle = 'simple' | 'numbered' | 'check' | 'bullet' | 'arrow';

export interface TermsBlock extends BaseBlock {
  type: 'terms';
  title: string;
  items: string[];
  style: TermsListStyle;
  // New advanced properties
  theme?: TermsTheme;
  variant?: TermsVariant;
  icon?: string; // lucide icon name
  showIcon?: boolean;
  collapsible?: boolean;
  expandedByDefault?: boolean;
  customColor?: string; // override theme color
  textSize?: 'small' | 'normal' | 'large';
  content?: string; // alternative to items - for AI generated content
  alignment?: 'right' | 'center' | 'left';
}

export interface DualSignatureBlock extends BaseBlock {
  type: 'dual_signature';
  leftLabel: string;
  rightLabel: string;
  leftName?: string;
  rightName?: string;
  showDate?: boolean;
  leftSignatureData?: string;
  rightSignatureData?: string;
}

export interface BenefitsBlock extends BaseBlock {
  type: 'benefits';
  title: string;
  items: { icon?: string; text: string }[];
  columns: 1 | 2;
  iconColor?: string;
  alignment?: 'right' | 'center' | 'left';
}

// Timeline/Milestones block
export interface TimelineBlock extends BaseBlock {
  type: 'timeline';
  title: string;
  items: {
    id: string;
    title: string;
    description?: string;
    date?: string;
    icon?: string;
  }[];
  layout: 'vertical' | 'horizontal';
  color?: string;
  alignment?: 'right' | 'center' | 'left';
}

// Team members block
export interface TeamBlock extends BaseBlock {
  type: 'team';
  title: string;
  members: {
    id: string;
    name: string;
    role: string;
    image?: string;
    description?: string;
  }[];
  columns: 2 | 3 | 4;
  alignment?: 'right' | 'center' | 'left';
}

// Testimonials block
export interface TestimonialsBlock extends BaseBlock {
  type: 'testimonials';
  title: string;
  items: {
    id: string;
    content: string;
    author: string;
    company?: string;
    image?: string;
    rating?: number;
  }[];
  layout: 'cards' | 'simple';
  alignment?: 'right' | 'center' | 'left';
}

// Package comparison block
export interface PackagesBlock extends BaseBlock {
  type: 'packages';
  title: string;
  packages: {
    id: string;
    name: string;
    price: number;
    currency: string;
    period?: string;
    description?: string;
    features: string[];
    highlighted?: boolean;
  }[];
}

// FAQ/Accordion block
export interface FAQBlock extends BaseBlock {
  type: 'faq';
  title: string;
  items: {
    id: string;
    question: string;
    answer: string;
  }[];
  alignment?: 'right' | 'center' | 'left';
}

// Client logos block
export interface ClientLogosBlock extends BaseBlock {
  type: 'client_logos';
  title: string;
  logos: {
    id: string;
    src: string;
    alt: string;
    url?: string;
  }[];
  columns: 3 | 4 | 5 | 6;
  grayscale?: boolean;
}

// Stats/Numbers block
export interface StatsBlock extends BaseBlock {
  type: 'stats';
  title?: string;
  items: {
    id: string;
    value: string;
    label: string;
    icon?: string;
  }[];
  columns: 2 | 3 | 4;
  color?: string;
  alignment?: 'right' | 'center' | 'left';
}

export interface AttachmentFile {
  id: string;
  name: string;
  size: number;
  type: string;
  url: string;
}

export interface AttachmentsBlock extends BaseBlock {
  type: 'attachments';
  title: string;
  files: AttachmentFile[];
}

export interface HtmlBlock extends BaseBlock {
  type: 'html';
  content: string;
  height?: number;
}

export type Block =
  | TextBlock
  | HeadingBlock
  | ImageBlock
  | PriceTableBlock
  | QuoteBlock
  | DividerBlock
  | SpacerBlock
  | VideoBlock
  | GalleryBlock
  | ListBlock
  | SignatureBlock
  | CoverBlock
  | TermsBlock
  | DualSignatureBlock
  | BenefitsBlock
  | TimelineBlock
  | TeamBlock
  | TestimonialsBlock
  | PackagesBlock
  | FAQBlock
  | ClientLogosBlock
  | StatsBlock
  | AttachmentsBlock
  | HtmlBlock;

// Block Templates for sidebar
export interface BlockTemplate {
  type: BlockType;
  label: string;
  labelHe: string;
  icon: string;
  description?: string;
  descriptionHe?: string;
  defaultContent: Partial<Block>;
}

export const blockTemplates: BlockTemplate[] = [
  {
    type: 'text',
    label: 'Text',
    labelHe: 'Text',
    icon: 'Type',
    description: 'Free text paragraph with styling',
    descriptionHe: 'פסקת טקסט חופשי עם עיצוב',
    defaultContent: { content: '', layout: 'standard' }
  },
  {
    type: 'heading',
    label: 'Heading',
    labelHe: 'Heading',
    icon: 'Heading',
    description: 'Large title to divide proposal sections',
    descriptionHe: 'כותרת גדולה לחלוקת ההצעה',
    defaultContent: { content: '', level: 2 }
  },
  {
    type: 'image',
    label: 'Image',
    labelHe: 'Image',
    icon: 'Image',
    description: 'Single image with optional caption',
    descriptionHe: 'תמונה בודדת עם כיתוב אופציונלי',
    defaultContent: { src: '', width: 'full' }
  },
  {
    type: 'price_table',
    label: 'Price Table',
    labelHe: 'Price Table',
    icon: 'Table',
    description: 'Line items with prices and totals',
    descriptionHe: 'טבלת פריטים ומחירים לחישוב עלות',
    defaultContent: { items: [], showQuantity: true, showUnitPrice: true }
  },
  {
    type: 'quote',
    label: 'Quote',
    labelHe: 'Quote',
    icon: 'Quote',
    description: 'Highlighted quote with unique styling',
    descriptionHe: 'ציטוט מודגש בעיצוב ייחודי',
    defaultContent: { content: '' }
  },
  {
    type: 'divider',
    label: 'Divider',
    labelHe: 'Divider',
    icon: 'Minus',
    description: 'Horizontal line between sections',
    descriptionHe: 'קו הפרדה בין חלקים',
    defaultContent: { style: 'solid' }
  },
  {
    type: 'spacer',
    label: 'Spacer',
    labelHe: 'Spacer',
    icon: 'Space',
    description: 'Vertical space between blocks',
    descriptionHe: 'רווח אנכי בין בלוקים',
    defaultContent: { height: 'medium' }
  },
  {
    type: 'list',
    label: 'List',
    labelHe: 'List',
    icon: 'List',
    description: 'Bulleted or numbered list',
    descriptionHe: 'רשימת נקודות או ממוספרת',
    defaultContent: { items: [''], style: 'bullet' }
  },
  {
    type: 'gallery',
    label: 'Gallery',
    labelHe: 'Gallery',
    icon: 'LayoutGrid',
    description: 'Image gallery in columns',
    descriptionHe: 'גלריית תמונות בעמודות',
    defaultContent: { images: [], columns: 3 }
  },
  {
    type: 'video',
    label: 'Video',
    labelHe: 'Video',
    icon: 'Video',
    description: 'Embed video from YouTube or Vimeo',
    descriptionHe: 'הטמעת וידאו מ-YouTube או Vimeo',
    defaultContent: { url: '' }
  },
  {
    type: 'cover',
    label: 'Cover',
    labelHe: 'Cover Page',
    icon: 'ImageIcon',
    description: 'Cover page with background, logo and title',
    descriptionHe: 'עמוד שער עם רקע, לוגו וכותרת',
    defaultContent: {
      title: 'Proposal',
      subtitle: '',
      overlay: true,
      overlayOpacity: 40,
      coverSize: 'top',
      textAlignment: 'right',
      showDate: true,
      showContact: false,
      logoPosition: 'top-right',
      logoSize: 'medium'
    }
  },
  {
    type: 'signature',
    label: 'Signature',
    labelHe: 'Signature',
    icon: 'PenTool',
    description: 'Digital signature field for client',
    descriptionHe: 'שדה חתימה דיגיטלית ללקוח',
    defaultContent: { label: 'Client Signature', required: true }
  },
  {
    type: 'terms',
    label: 'Terms',
    labelHe: 'Terms',
    icon: 'FileCheck',
    description: 'Terms & conditions with checkmarks',
    descriptionHe: 'תנאים והגבלות עם סימוני צ׳ק',
    defaultContent: {
      title: 'Terms & Conditions',
      items: ['First term', 'Second term', 'Third term'],
      style: 'check',
      theme: 'general',
      variant: 'filled',
      showIcon: true
    }
  },
  {
    type: 'dual_signature',
    label: 'Dual Signature',
    labelHe: 'Dual Signature',
    icon: 'Users',
    description: 'Dual signature — provider and client',
    descriptionHe: 'חתימה כפולה — ספק ולקוח',
    defaultContent: {
      leftLabel: 'Service Provider Signature',
      rightLabel: 'Client Signature',
      showDate: true
    }
  },
  {
    type: 'benefits',
    label: 'Benefits',
    labelHe: 'Benefits',
    icon: 'Star',
    description: 'Benefits list with icons',
    descriptionHe: 'רשימת יתרונות עם אייקונים',
    defaultContent: {
      title: 'System Benefits',
      items: [
        { text: 'First benefit' },
        { text: 'Second benefit' },
        { text: 'Third benefit' }
      ],
      columns: 1,
      iconColor: '#10b981'
    }
  },
  {
    type: 'timeline',
    label: 'Timeline',
    labelHe: 'Timeline',
    icon: 'GitBranch',
    description: 'Timeline for project phases',
    descriptionHe: 'ציר זמן לשלבי הפרויקט',
    defaultContent: {
      title: 'Project Phases',
      items: [
        { id: '1', title: 'Phase 1', description: 'Phase description', date: '' },
        { id: '2', title: 'Phase 2', description: 'Phase description', date: '' },
        { id: '3', title: 'Phase 3', description: 'Phase description', date: '' }
      ],
      layout: 'vertical',
      color: '#FF69B4'
    }
  },
  {
    type: 'team',
    label: 'Team',
    labelHe: 'Team',
    icon: 'Users',
    description: 'Team members with photos and roles',
    descriptionHe: 'חברי הצוות עם תמונות ותפקידים',
    defaultContent: {
      title: 'Our Team',
      members: [
        { id: '1', name: 'Full Name', role: 'Role', description: '' }
      ],
      columns: 3
    }
  },
  {
    type: 'testimonials',
    label: 'Testimonials',
    labelHe: 'Testimonials',
    icon: 'MessageSquare',
    description: 'Reviews from previous clients',
    descriptionHe: 'המלצות לקוחות קודמים',
    defaultContent: {
      title: 'What Our Clients Say',
      items: [
        { id: '1', content: 'Testimonial from a satisfied client...', author: 'Client Name', company: 'Company Name', rating: 5 }
      ],
      layout: 'cards'
    }
  },
  {
    type: 'packages',
    label: 'Packages',
    labelHe: 'Packages',
    icon: 'Package',
    description: 'Pricing packages for comparison',
    descriptionHe: 'חבילות תמחור להשוואה',
    defaultContent: {
      title: 'Our Packages',
      packages: [
        { id: '1', name: 'Basic', price: 499, currency: '$', features: ['Feature 1', 'Feature 2'] },
        { id: '2', name: 'Advanced', price: 999, currency: '$', features: ['Feature 1', 'Feature 2', 'Feature 3'], highlighted: true },
        { id: '3', name: 'Premium', price: 1999, currency: '$', features: ['Feature 1', 'Feature 2', 'Feature 3', 'Feature 4'] }
      ]
    }
  },
  {
    type: 'faq',
    label: 'FAQ',
    labelHe: 'FAQ',
    icon: 'HelpCircle',
    description: 'Frequently asked questions',
    descriptionHe: 'שאלות ותשובות נפוצות',
    defaultContent: {
      title: 'Frequently Asked Questions',
      items: [
        { id: '1', question: 'First question?', answer: 'Answer to the first question' },
        { id: '2', question: 'Second question?', answer: 'Answer to the second question' }
      ]
    }
  },
  {
    type: 'client_logos',
    label: 'Client Logos',
    labelHe: 'Client Logos',
    icon: 'Building2',
    description: 'Logos of your clients',
    descriptionHe: 'לוגואים של לקוחות',
    defaultContent: {
      title: 'Clients Who Trust Us',
      logos: [],
      columns: 4,
      grayscale: true
    }
  },
  {
    type: 'stats',
    label: 'Stats',
    labelHe: 'Stats',
    icon: 'TrendingUp',
    description: 'Impressive numbers and stats',
    descriptionHe: 'מספרים ונתונים מרשימים',
    defaultContent: {
      title: '',
      items: [
        { id: '1', value: '10+', label: 'Years of Experience' },
        { id: '2', value: '500+', label: 'Projects' },
        { id: '3', value: '100%', label: 'Satisfaction' }
      ],
      columns: 3,
      color: '#FF69B4'
    }
  },
  {
    type: 'attachments',
    label: 'Attachments',
    labelHe: 'Attachments',
    icon: 'Paperclip',
    description: 'Attached files for download',
    descriptionHe: 'קבצים מצורפים להורדה',
    defaultContent: { title: 'Attachments', files: [] }
  },
  {
    type: 'html',
    label: 'HTML',
    labelHe: 'HTML',
    icon: 'Code2',
    description: 'Custom HTML/CSS code',
    descriptionHe: 'קוד HTML/CSS מותאם אישית',
    defaultContent: { content: '', height: 200 }
  }
];

// Helper to create a new block
export function createBlock(type: BlockType, order: number): Block {
  const id = `block-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const template = blockTemplates.find(t => t.type === type);

  const base = {
    id,
    type,
    order,
    ...template?.defaultContent
  };

  return base as Block;
}
